import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Download, FileText, FolderOpen, Clock, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { saveWorkflowOutputFile, downloadStoredFile } from '@/lib/workflow-output';
import { formatDateTime } from '@/lib/format';


interface WorkflowOutputFilesProps {
  runId: string;
  runVersion: number;
}

export const WorkflowOutputFiles = ({ runId, runVersion }: WorkflowOutputFilesProps) => {
  const [generating, setGenerating] = useState<string | null>(null);
  const navigate = useNavigate();

  // Query files already generated for this run
  const { data: files, isLoading, refetch } = useQuery({
    queryKey: ['workflow-output-files', runId, runVersion],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('file_storage')
        .select('*')
        .eq('folder_path', `/Workflow Outputs/v${runVersion}/`)
        .eq('is_folder', false)
        .order('file_name');
      if (error) throw error;
      return data || [];
    },
  });


  const generateCSV = async (dataType: 'cleaned' | 'predicted', label: string) => {
    setGenerating(dataType);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const result = await saveWorkflowOutputFile({
        userId: user.id,
        runId,
        runVersion,
        dataType,
        label,
      });

      if (!result.saved) {
        toast.error(result.reason || 'Nothing to export yet');
        return;
      }

      toast.success(`${label} file generated successfully`);
      refetch();
    } catch (err: any) {
      console.error('File generation error:', err);
      toast.error(`Failed to generate file: ${err.message}`);
    } finally {
      setGenerating(null);
    }
  };

  const downloadFile = async (filePath: string, fileName: string) => {
    try {
      await downloadStoredFile(filePath, fileName);
    } catch (err: any) {
      toast.error(`Download failed: ${err.message}`);
    }
  };


  return (
    <div className="space-y-6">
      {/* Generate Buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <FileText className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-sm">Cleaned Dataset</p>
              <p className="text-xs text-muted-foreground">CSV file with AI-cleaned CRM data</p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => generateCSV('cleaned', 'Cleaned Dataset')}
            disabled={generating === 'cleaned'}
            className="w-full"
          >
            {generating === 'cleaned' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
            Generate & Save
          </Button>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-sm">Prediction Results</p>
              <p className="text-xs text-muted-foreground">CSV file with AI predictions and scores</p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => generateCSV('predicted', 'Prediction Results')}
            disabled={generating === 'predicted'}
            className="w-full"
          >
            {generating === 'predicted' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
            Generate & Save
          </Button>
        </Card>
      </div>

      {/* Existing Files */}
      <div>
        <h4 className="font-medium mb-3 flex items-center gap-2">
          <FolderOpen className="h-4 w-4 text-primary" />
          Generated Files
        </h4>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading files…</p>
        ) : !files?.length ? (
          <Card className="p-6 text-center">
            <p className="text-sm text-muted-foreground">No files generated yet. Click the buttons above to create output files.</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {files.map(file => (
              <Card key={file.id} className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{file.file_name}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDateTime(file.created_at, 'date')}
                      </span>
                      <span>v{runVersion}</span>
                      {file.size && <span>{(file.size / 1024).toFixed(1)} KB</span>}
                    </div>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => downloadFile(file.file_path, file.file_name)}>
                  <Download className="h-4 w-4" />
                </Button>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Button variant="outline" size="sm" onClick={() => navigate('/folder-management')} className="flex items-center gap-2">
        <FolderOpen className="h-4 w-4" />
        Open Folder Management
      </Button>
    </div>
  );
};
